import { add } from "dinero.js"

import { toDinero, toMonetaryAmount } from "../../../../../modules/currency/currencyLib"
import type { MonetaryAmount } from "../../../../../modules/currency/currencyTypes"
import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { ExtraServiceActionType } from "../../../../../modules/merchant/admin/extraServices/extraServicesTypes"
import {
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    CreateOrderDialogType,
    CreateOrderFlowType,
    OrderLineType,
} from "../../createOrderTypes"
import { getCreateOrderDialogTypeFlags } from "../../hooks/useCreateOrderDialogType"
import { DeliveryOrderLine, DeliveryTimesField, DynamicConfigKey, ProductWishOrderLine } from "./formTypes"

type DeliveryFromApi = MeetingServiceOrder["deliveries"][number]
type OrderLineFromApi = DeliveryFromApi["orderLines"][number]
type ProductLineFromApi = Extract<OrderLineFromApi, { type?: undefined }>

function isProductLine(line: OrderLineFromApi): line is ProductLineFromApi {
    return line.type === undefined
}

function addMonetaryAmounts(a: MonetaryAmount, b: MonetaryAmount): MonetaryAmount {
    return toMonetaryAmount(add(toDinero(a), toDinero(b)))
}

// Extra services are charged once per delivery, so a repeated one is dropped rather than summed.
function mergeOrderLines(target: OrderLineFromApi[], source: OrderLineFromApi[]): OrderLineFromApi[] {
    return source.reduce((lines, line) => {
        if (line.type === OrderLineType.EXTRA_SERVICE) {
            const isRepeated = lines.some(
                (l) => l.type === OrderLineType.EXTRA_SERVICE && l.productId === line.productId,
            )
            return isRepeated ? lines : [...lines, line]
        }
        if (isProductLine(line)) {
            const existing = lines.find(
                (l): l is ProductLineFromApi => isProductLine(l) && l.productId === line.productId,
            )
            if (!existing) return [...lines, line]
            const merged: ProductLineFromApi = {
                ...existing,
                items: existing.items + line.items,
                price: addMonetaryAmounts(existing.price, line.price),
            }
            return lines.map((l) => (l === existing ? merged : l))
        }
        return [...lines, line]
    }, target)
}

// The form identifies a delivery by its HH:mm alone, so API deliveries sharing one would be edited and
// saved as duplicates; they are folded into one. The API does not guarantee an order, hence the sort.
export function mergeDeliveriesByTime(
    deliveries: ReadonlyArray<DeliveryFromApi>,
    dateTimeFactory: DateTimeFactory,
): DeliveryFromApi[] {
    const sorted = [...deliveries].sort(
        (a, b) =>
            dateTimeFactory.fromApi(a.deliveryTime).toMillis() - dateTimeFactory.fromApi(b.deliveryTime).toMillis(),
    )
    const byTime = new Map<string, DeliveryFromApi>()
    for (const delivery of sorted) {
        const time = dateTimeFactory.toHourMinute(dateTimeFactory.fromApi(delivery.deliveryTime))
        const existing = byTime.get(time)
        byTime.set(
            time,
            existing
                ? { ...existing, orderLines: mergeOrderLines(existing.orderLines, delivery.orderLines) }
                : delivery,
        )
    }
    return [...byTime.values()]
}

// The index keeps two same-named wishes in one delivery apart. It must stay deterministic: this runs
// again on every reinitialize/merge, and a random id would break the deep-equal comparisons that sync
// the two forms.
function buildLegacyExtraServiceLineId(orderLineId: number): string {
    return `legacy-extra-service_${orderLineId}`
}

function buildProductWishLineId(time: string, index: number, name: string): string {
    return `${time}_${index}_${name}`
}

// The form id of a saved order line. The refund snapshot has to derive the very same id, so both
// sides read it from here instead of rebuilding the shape.
export function getOrderLineFormId(line: OrderLineFromApi, time: string, index: number): string | number {
    if (line.type === OrderLineType.LEGACY_EXTRA_SERVICE) return buildLegacyExtraServiceLineId(line.id)
    return line.type === DynamicConfigKey.PRODUCT_WISH ? buildProductWishLineId(time, index, line.name) : line.productId
}

type MultipleDeliveriesState = {
    mode?: CreateOrderDialogMode
    dialogType: CreateOrderDialogType
    flowConfig?: CreateOrderDialogFlow
    orderDetails: MeetingServiceOrder
    dateTimeFactory: DateTimeFactory
}

export function buildMultipleDeliveriesState({
    mode,
    dialogType,
    flowConfig,
    orderDetails,
    dateTimeFactory,
}: MultipleDeliveriesState): DeliveryTimesField | null {
    const isOutlookFlow = flowConfig?.flow === CreateOrderFlowType.OUTLOOK
    if (mode === CreateOrderDialogMode.CREATE && !isOutlookFlow) return null
    const { isSupplementaryOrder } = getCreateOrderDialogTypeFlags(dialogType)

    const meetingStartIso = orderDetails.meetingDetails?.startDateTime
    // Chronological, so deliveries[0] is the earliest and anchors the date/active time.
    const deliveries = mergeDeliveriesByTime(orderDetails.deliveries ?? [], dateTimeFactory)
    const useMeetingStart = isOutlookFlow && !!meetingStartIso && !deliveries.length

    const meetingStart = meetingStartIso ? dateTimeFactory.fromApi(meetingStartIso) : null

    const baseDateTime = useMeetingStart
        ? meetingStart
        : deliveries.length
          ? dateTimeFactory.fromApi(deliveries[0].deliveryTime)
          : null

    // The delivery date is independent of the booking date — catering can legitimately be
    // delivered on another day than the meeting — so a saved delivery keeps its own date
    // even when the booking sits on a different day.
    const activeTime = baseDateTime ? dateTimeFactory.toHourMinute(baseDateTime) : null
    const deliveryDate =
        mode === CreateOrderDialogMode.DUPLICATE ? null : (baseDateTime?.toFormat("yyyy-LL-dd") ?? null)

    const deliveryTimes = useMeetingStart
        ? [
              {
                  time: activeTime,
                  orderLines: [], // No order lines from meetingDetails
              },
          ]
        : deliveries.map((d) => {
              const time = dateTimeFactory.toHourMinute(dateTimeFactory.fromApi(d.deliveryTime))
              const orderLines: DeliveryOrderLine[] = isSupplementaryOrder
                  ? []
                  : d.orderLines.map((l, index): DeliveryOrderLine => {
                        if (l.type === OrderLineType.LEGACY_EXTRA_SERVICE) {
                            return {
                                type: OrderLineType.LEGACY_EXTRA_SERVICE,
                                id: buildLegacyExtraServiceLineId(l.id),
                                orderLineId: l.id,
                                productId: l.productId,
                                name: l.name,
                                quantity: l.items,
                                price: l.itemPrice,
                                isActive: true,
                                isExtraService: true,
                            }
                        }
                        if (l.type === OrderLineType.EXTRA_SERVICE) {
                            const isAutoAdded =
                                l.action === ExtraServiceActionType.ADD_ALWAYS ||
                                l.action === ExtraServiceActionType.ADD_WHEN_PICK_UP ||
                                l.action === ExtraServiceActionType.ADD_WHEN_DELIVERED
                            return {
                                id: l.productId,
                                name: l.name,
                                marketingDetails: undefined,
                                quantity: l.items,
                                isActive: true,
                                price: l.price,
                                isExtraService: true,
                                isAdminOnly: l.isAdminOnly,
                                ...(l.action ? { action: l.action } : {}),
                                ...(isAutoAdded ? { isAutoAddedExtraService: true } : {}),
                            }
                        }
                        if (l.type === DynamicConfigKey.PRODUCT_WISH) {
                            const wishLine: ProductWishOrderLine = {
                                type: DynamicConfigKey.PRODUCT_WISH,
                                id: buildProductWishLineId(time, index, l.name),
                                name: l.name,
                                quantity: l.items,
                                price: l.itemPrice,
                            }
                            return wishLine
                        }
                        return {
                            id: l.productId,
                            name: l.name,
                            marketingDetails: undefined,
                            quantity: l.items,
                            isActive: true,
                            price: l.itemPrice,
                            isExtraService: false,
                            priceListId: l.priceListId,
                            isAdminOnly: l.isAdminOnly,
                        }
                    })
              return { time, orderLines }
          })

    return { activeTime, deliveryDate, deliveryTimes }
}
